import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

export interface ProjectContact {
  contactId: number;
  name: string;
  email: string | null;
  isActive: boolean;
  isPrimary: boolean;
}

export interface ProjectContactLink {
  contactId: number;
  isPrimary: boolean;
}

const INACTIVE_SUFFIX = /\s*\(inactive\)\s*$/i;

@Injectable({
  providedIn: 'root'
})
export class ProjectContactsService {
  private readonly baseUrl = environment.main_url + '/agilelegal';

  constructor(private readonly http: HttpClient) { }

  getOrganizationContacts(organizationId: number): Observable<ProjectContact[] | null> {
    return this.http.get<any>(`${this.baseUrl}/organizations/${organizationId}/contacts`).pipe(
      map(res => this.rows(res).map((c: any) => this.toContact(c.contactId, [c.firstName, c.lastName].filter(Boolean).join(' '), c.email, false))),
      catchError(() => of(null))
    );
  }

  getProjectContacts(projectId: number): Observable<ProjectContact[] | null> {
    return this.http.get<any>(`${this.baseUrl}/projectcontacts/${projectId}`).pipe(
      map(res => this.rows(res).map((c: any) => this.toContact(c.contactId, c.contactName, c.contactEmail, String(c.primaryContact ?? '').trim().toLowerCase() === 'yes'))),
      catchError(() => of(null))
    );
  }

  associateContacts(projectId: number, links: ProjectContactLink[]): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/projectcontacts/${projectId}`, {
      contacts: links.map(link => ({ contactId: link.contactId, primaryContact: link.isPrimary ? 'Yes' : 'No' }))
    });
  }

  removeContacts(projectId: number, contactIds: number[]): Observable<any> {
    return this.http.delete<any>(`${this.baseUrl}/projectcontacts/${projectId}`, { body: { contactIds } });
  }

  private rows(res: any): any[] {
    return Array.isArray(res?.data) ? res.data : [];
  }

  private toContact(contactId: number, rawName: string | null, email: string | null, isPrimary: boolean): ProjectContact {
    const fullName = String(rawName ?? '').split(/\s+/).filter(part => part && part.toLowerCase() !== 'null').join(' ');
    return {
      contactId,
      name: fullName.replace(INACTIVE_SUFFIX, '') || email || '',
      email: email ?? null,
      isActive: !INACTIVE_SUFFIX.test(fullName),
      isPrimary
    };
  }
}
